// src/Pages/ContractView.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const yearsRemainingLabel = (endDate) => {
  if (!endDate) return '—';
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return '—';
  const diffMs = end.getTime() - Date.now();
  const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  if (diffYears <= 0) return 'Expired';
  if (diffYears < 1) return 'Less than 1 year';
  return `${Math.floor(diffYears)} years left`;
};

const humanContractName = (storedName = '') => {
  if (!storedName) return '';
  const marker = '__';
  const idx = storedName.indexOf(marker);
  return idx >= 0 ? storedName.slice(idx + marker.length) : storedName;
};

const buildProjectFacts = (project) => {
  if (!project) return [];
  return [
    {
      label: 'Label Share',
      value:
        typeof project.labelShare === 'number'
          ? `${project.labelShare}%`
          : project.labelShare || '—',
    },
    {
      label: 'Term',
      value:
        project.termYears !== undefined && project.termYears !== null
          ? `${project.termYears} years`
          : project.termYears || '—',
    },
    { label: 'Release Date', value: formatDate(project.releaseDate) },
    {
      label: 'End Date',
      value: formatDate(project.endDate),
      secondary: yearsRemainingLabel(project.endDate),
    },
  ];
};

function ContractView() {
  const { id } = useParams(); // project ID
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfBust, setPdfBust] = useState(0);
  const [selectedContract, setSelectedContract] = useState('');

  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api(`/api/projects/${id}`);
        if (!res.ok) throw new Error(`Failed to fetch project (${res.status})`);
        const projectData = await res.json();
        const names = Array.isArray(projectData.contracts) && projectData.contracts.length
          ? projectData.contracts
          : (projectData.contract ? [projectData.contract] : []);
        setSelectedContract(projectData.contract || names[names.length - 1] || '');
        setProject(projectData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div style={{ padding: 20, color: '#fff' }}>
        <p>Loading contract...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: '#fff' }}>
        <p style={{ color: 'red' }}>Error: {error}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: 10,
            padding: '10px 20px',
            background: '#555',
            border: 'none',
            color: '#fff',
            borderRadius: 5,
            cursor: 'pointer',
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: 20, color: '#fff' }}>
        <p>Project not found</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: 10,
            padding: '10px 20px',
            background: '#555',
            border: 'none',
            color: '#fff',
            borderRadius: 5,
            cursor: 'pointer',
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const projectFacts = buildProjectFacts(project);
  const contractNames = Array.isArray(project.contracts) && project.contracts.length
    ? project.contracts
    : (project.contract ? [project.contract] : []);
  const activeContract = selectedContract || project.contract || contractNames[contractNames.length - 1] || '';

  return (
    <div style={{ padding: 20, color: '#f5f5f5', minHeight: '100vh', background: '#0b0b0b' }}>
      {/* back */}
      <button
        onClick={() => navigate(-1)}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#fff',
          fontSize: 24,
          cursor: 'pointer',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          style={{ marginRight: 8, fill: '#333' }}
        >
          <path
            d="M10 6 L8.59 7.41 13.17 12 l-4.58 4.59L10 18 l6-6z"
            transform="rotate(180,12,12)"
          />
        </svg>
      </button>

      <h1 style={{ marginBottom: 10, fontSize: 28, fontWeight: 700 }}>
        Contract: {project.title}
      </h1>
      <p style={{ marginTop: 0, marginBottom: 30, color: '#9da0a8' }}>
        &nbsp;
      </p>

      <div
        style={{
          background: '#1e1e1e',
          borderRadius: 12,
          border: '1px solid #262626',
          padding: 24,
          width: 'min(900px, 90vw)',
          marginBottom: 32,
          boxShadow: '0 15px 35px rgba(0,0,0,0.35)',
        }}
      >
        <h2 style={{ margin: 0, marginBottom: 16 }}>Project Facts</h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
          }}
        >
        {projectFacts.map(({ label, value, secondary }) => (
            <div
              key={label}
              style={{
                background: '#151515',
                borderRadius: 10,
                border: '1px solid #232323',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minHeight: 70,
              }}
            >
              <span style={{ fontSize: 12, textTransform: 'uppercase', color: '#80838b', letterSpacing: 0.5 }}>
                {label}
              </span>
              <span style={{ fontSize: 20, fontWeight: 600, color: '#f5f5f5' }}>{value || '—'}</span>
              {secondary && secondary !== '—' && (
                <span style={{ fontSize: 13, color: secondary === 'Expired' ? '#ff6b6b' : '#9da0a8' }}>
                  {secondary}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* pdf preview */}
      {contractNames.length ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Contract{contractNames.length > 1 ? 's' : ''}</h3>
            <button
              onClick={() => setPdfBust(Date.now())}
              style={{
                padding: '6px 12px',
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Refresh
            </button>
          </div>

          {contractNames.length > 1 && (
            <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {contractNames.map((name) => {
                const isActive = name === activeContract;
                return (
                  <button
                    key={name}
                    onClick={() => setSelectedContract(name)}
                    style={{
                      padding: '6px 10px',
                      background: isActive ? '#444' : '#2b2b2b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                    title={name}
                  >
                    {humanContractName(name)}
                  </button>
                );
              })}
            </div>
          )}
          <div
            style={{
              width: 'min(1100px, 95vw)',
              height: '80vh',
              background: '#121212',
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid #1f1f1f',
              boxShadow: '0 15px 35px rgba(0,0,0,0.4)',
            }}
          >
            <iframe
              title="Contract PDF"
              src={`${BACKEND_URL}/uploads/${encodeURIComponent(activeContract)}${pdfBust ? `?v=${pdfBust}` : ''}`}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      ) : (
        <p style={{ marginTop: 20, color: '#999' }}>
          No contract file attached to this project
        </p>
      )}
    </div>
  );
}

export default ContractView;

